import { ErrorHelperService, ToasterService } from '#services';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

interface ChunkLoadError extends Error {
  type?: string;
  request?: string;
}

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private _toastrService = inject(ToasterService);
  private _errorHelperService = inject(ErrorHelperService);
  private _router = inject(Router);
  private _chunkFailedReloads = new Set<string>();

  handleError(err: unknown): void {
    console.error(err);

    // Handle chunk loading errors with automatic reload
    if (this._isChunkLoadError(err)) {
      this._handleChunkLoadError(err as ChunkLoadError);
      return;
    }

    const { detail, summary = '' } = this._errorHelperService.handleError(err);

    this._toastrService.showError(detail, summary);
  }

  private _isChunkLoadError(err: unknown): boolean {
    const error = err as ChunkLoadError;
    return (
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('Failed to fetch dynamically imported module')
    );
  }

  private _handleChunkLoadError(err: ChunkLoadError): void {
    const currentUrl = this._router.url;

    // Prevent infinite reload loop
    if (this._chunkFailedReloads.has(currentUrl)) {
      console.error('Chunk load failed after reload, clearing cache and reloading...', err);
      this._chunkFailedReloads.delete(currentUrl);
      // Force hard reload to clear all caches
      window.location.reload();
      return;
    }

    console.warn('Chunk load failed, reloading page...', err);
    this._chunkFailedReloads.add(currentUrl);

    // Reload the page
    window.location.reload();
  }
}
